/**
 * Auth Client
 * Provides contract-based access to authentication endpoints.
 * Follows Simple CRUD Pattern from client factory architecture.
 * @module api/clients/auth
 */
import api from "@/services/api"
import { createContractClient } from "../contractClient"
import {
  AuthLoginResponseSchema,
  AuthTokenCheckResponseSchema,
  CsrfTokenResponseSchema,
} from "../contracts/auth"
import {
  normalizeAuthResponse,
  normalizeTokenCheckResponse,
} from "../normalizers/auth.normalizer"

// LLM:METADATA
// :hierarchy: [Domain:Authentication | API_Client | Factory]
// :relates-to: uses: "createContractClient", uses: "auth contracts", uses: "auth normalizers", required-by: "useAuth.js"
// :rationale: "Provides type-safe auth API calls with contract validation and response normalization for 3 auth endpoints (login, token check, CSRF)"
// :contract: pre: "api client initialized", post: "Client object with login/checkToken/csrfToken methods", invariant: "All responses validated against schemas and normalized"
// :decision_cache: "Chose simple client pattern for auth due to low endpoint count (3) and no complex CRUD operations. Normalizers handle legacy vs new response format differences [AUTH-CLIENT-001]"
// :references: SPEC: "Phase 3B Client Factory Implementation", ARCH: "client-factory-architecture.md Pattern 1"
// LLM:END

/**
 * Create authentication client with contract validation
 * @returns {Object} Auth client with login, checkToken, and csrfToken methods
 */
export function createAuthClient() {
  // Login endpoint client with normalization
  const loginClient = createContractClient(
    api,
    "auth",
    AuthLoginResponseSchema,
    normalizeAuthResponse
  )

  // Token check endpoint client with normalization
  const tokenCheckClient = createContractClient(
    api,
    "auth/auth_token_check",
    AuthTokenCheckResponseSchema,
    normalizeTokenCheckResponse
  )

  // CSRF token endpoint client (no normalization needed)
  const csrfClient = createContractClient(
    api,
    "auth/csrf-token",
    CsrfTokenResponseSchema
  )

  return {
    /**
     * Authenticate user with email and password
     *
     * @param {string} email - User email address
     * @param {string} password - User password
     * @returns {Promise<AuthLoginResponse>} Normalized login response
     * @returns {boolean} returns.success - Success indicator
     * @returns {string} [returns.token] - Auth token on success
     * @returns {number} [returns.id] - User ID on success
     * @returns {string} [returns.role] - User role on success
     * @returns {string} [returns.error] - Error message on failure
     *
     * @example
     * const result = await authClient.login('user@example.com', 'password123')
     * if (result.success) {
     *   console.log('Logged in as:', result.role)
     * } else {
     *   console.error('Login failed:', result.error)
     * }
     */
    login: async (email, password) => {
      return loginClient.post({ email: email.trim(), pass: password })
    },

    /**
     * Validate current authentication token
     *
     * @param {string} token - Auth token to validate
     * @param {string} role - User role for validation
     * @returns {Promise<AuthTokenCheckResponse>} Normalized token check response
     * @returns {boolean} returns.checked - Token validity indicator
     * @returns {Object|null} returns.user - User object if token valid
     *
     * @example
     * const result = await authClient.checkToken(token, role)
     * if (result.checked) {
     *   console.log('Token valid for user:', result.user)
     * } else {
     *   console.log('Token invalid or expired')
     * }
     */
    checkToken: async (token, role) => {
      return tokenCheckClient.post({ token, role })
    },

    /**
     * Fetch CSRF token for authenticated requests
     *
     * @returns {Promise<CsrfTokenResponse>} CSRF token response
     * @returns {string} returns.token - CSRF token
     *
     * @example
     * const { token } = await authClient.csrfToken()
     * console.log('CSRF token:', token)
     */
    csrfToken: async () => {
      return csrfClient.get("")
    },

    /**
     * Revoke backend auth token before client-side session purge.
     *
     * @returns {Promise<import('axios').AxiosResponse>} Backend logout response
     */
    logout: async () => {
      return api.post("auth/logout")
    },
  }
}

// Singleton instance for direct import
export const authClient = createAuthClient()
